import React from 'react';

import {Icon, WithTooltip} from '@albert-io/atomic';

import {TEACHER_RESTRICTION_MESSAGE} from './teacherRestriction.utils';

interface TeacherRestrictionIndicatorProps {
  className?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left' | 'right-start' | 'top-start' | 'bottom-start';
}

const TRIGGER_CLASS_NAME =
  'inline-flex cursor-default appearance-none items-center justify-center rounded-x0-half border-0 bg-transparent p-x0-half text-xs';

/**
 * No per-school breakdown or manage CTA: teachers can't read which schools
 * restrict a question, nor change restrictions. Trigger is a real `<button>`
 * so the explanation appears on focus, not just hover. Uses the default
 * (dark) tooltip styling — our usual tooltip, not a custom box.
 */
export function TeacherRestrictionIndicator({
  className,
  placement = 'right-start'
}: TeacherRestrictionIndicatorProps) {
  return (
    <WithTooltip
      as='button'
      focusable
      placement={placement}
      anchoredFixedPositionProps={{
        // Portal + viewport boundaries so the tooltip isn't clipped inside
        // overflow-hidden list rows and stays beside the lock (not flipped over it).
        usePortal: true,
        modifiers: {
          preventOverflow: {boundariesElement: 'viewport'},
          flip: {boundariesElement: 'viewport'}
        }
      }}
      content={TEACHER_RESTRICTION_MESSAGE}
      theme='light'
      wrapperProps={{
        type: 'button',
        'aria-label': 'Restricted content',
        className: className ? `${TRIGGER_CLASS_NAME} ${className}` : TRIGGER_CLASS_NAME,
        // Swallow the click so it doesn't trigger the enclosing row's navigation.
        onClick: (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Icon icon='lock' iconStyle='solid' color='secondary' size='inherit' />
    </WithTooltip>
  );
}

export default TeacherRestrictionIndicator;
